/**
 * Chat WebSocket Hook
 * Specialized hook for chat namespace with event handlers
 */

import { useEffect } from 'react';
import { useWebSocket } from './useWebSocket';
import type {
  ChatMessageCreatedEvent,
  ChatMessageUpdatedEvent,
  ChatMessageArchivedEvent,
  ChatLikeAddedEvent,
  ChatLikeRemovedEvent,
  ChatMessageSeenEvent,
  ChatMemberAddedEvent,
  ChatMemberRemovedEvent,
  ChatMemberRoleChangedEvent,
} from '@/api/types/chat.types';

interface ChatEventHandlers {
  /** Handler for new message created */
  onMessageCreated?: (event: ChatMessageCreatedEvent) => void;
  /** Handler for message updated */
  onMessageUpdated?: (event: ChatMessageUpdatedEvent) => void;
  /** Handler for message archived */
  onMessageArchived?: (event: ChatMessageArchivedEvent) => void;
  /** Handler for like/reaction added */
  onLikeAdded?: (event: ChatLikeAddedEvent) => void;
  /** Handler for like/reaction removed */
  onLikeRemoved?: (event: ChatLikeRemovedEvent) => void;
  /** Handler for message seen */
  onMessageSeen?: (event: ChatMessageSeenEvent) => void;
  /** Handler for member added to chat */
  onMemberAdded?: (event: ChatMemberAddedEvent) => void;
  /** Handler for member removed from chat */
  onMemberRemoved?: (event: ChatMemberRemovedEvent) => void;
  /** Handler for member role changed */
  onMemberRoleChanged?: (event: ChatMemberRoleChangedEvent) => void;
}

interface UseChatSocketOptions {
  /** Enable WebSocket connection */
  enabled?: boolean;
  /** Event handlers */
  handlers?: ChatEventHandlers;
}

interface UseChatSocketReturn {
  /** Socket.IO instance */
  socket: any;
  /** Connection status */
  connected: boolean;
  /** Manually connect to WebSocket */
  connect: () => void;
  /** Manually disconnect from WebSocket */
  disconnect: () => void;
  /** Reconnect with fresh token */
  reconnect: () => void;
}

/**
 * Chat-specific WebSocket hook
 * Automatically connects to /chat namespace and sets up event listeners
 *
 * @example
 * ```typescript
 * const { socket, connected } = useChatSocket({
 *   handlers: {
 *     onMessageCreated: (event) => {
 *       console.log('New message:', event.data);
 *       setMessages(prev => [...prev, event.data]);
 *     },
 *     onMessageUpdated: (event) => {
 *       console.log('Message updated:', event.data);
 *     },
 *   },
 * });
 * ```
 */
export function useChatSocket(
  options: UseChatSocketOptions = {}
): UseChatSocketReturn {
  const { enabled = true, handlers = {} } = options;

  const { socket, connected, connect, disconnect, reconnect } = useWebSocket({
    namespace: '/chat',
    enabled,
    autoConnect: true,
  });

  // Set up event listeners
  useEffect(() => {
    if (!socket || !connected) return;

    console.log('📡 Setting up chat event listeners');

    // Message created event
    if (handlers.onMessageCreated) {
      socket.on('chat.message.created', handlers.onMessageCreated);
    }

    // Message updated event
    if (handlers.onMessageUpdated) {
      socket.on('chat.message.updated', handlers.onMessageUpdated);
    }

    // Message archived event
    if (handlers.onMessageArchived) {
      socket.on('chat.message.archived', handlers.onMessageArchived);
    }

    // Like added event
    if (handlers.onLikeAdded) {
      socket.on('chat.like.added', handlers.onLikeAdded);
    }

    // Like removed event
    if (handlers.onLikeRemoved) {
      socket.on('chat.like.removed', handlers.onLikeRemoved);
    }

    // Message seen event
    if (handlers.onMessageSeen) {
      socket.on('chat.message.seen', handlers.onMessageSeen);
    }

    // Member added event
    if (handlers.onMemberAdded) {
      socket.on('chat.member.added', handlers.onMemberAdded);
    }

    // Member removed event
    if (handlers.onMemberRemoved) {
      socket.on('chat.member.removed', handlers.onMemberRemoved);
    }

    // Member role changed event
    if (handlers.onMemberRoleChanged) {
      socket.on('chat.member.role_changed', handlers.onMemberRoleChanged);
    }

    // Cleanup function - only remove the specific handlers we added
    return () => {
      console.log('🧹 Cleaning up chat event listeners');
      if (handlers.onMessageCreated) {
        socket.off('chat.message.created', handlers.onMessageCreated);
      }
      if (handlers.onMessageUpdated) {
        socket.off('chat.message.updated', handlers.onMessageUpdated);
      }
      if (handlers.onMessageArchived) {
        socket.off('chat.message.archived', handlers.onMessageArchived);
      }
      if (handlers.onLikeAdded) {
        socket.off('chat.like.added', handlers.onLikeAdded);
      }
      if (handlers.onLikeRemoved) {
        socket.off('chat.like.removed', handlers.onLikeRemoved);
      }
      if (handlers.onMessageSeen) {
        socket.off('chat.message.seen', handlers.onMessageSeen);
      }
      if (handlers.onMemberAdded) {
        socket.off('chat.member.added', handlers.onMemberAdded);
      }
      if (handlers.onMemberRemoved) {
        socket.off('chat.member.removed', handlers.onMemberRemoved);
      }
      if (handlers.onMemberRoleChanged) {
        socket.off('chat.member.role_changed', handlers.onMemberRoleChanged);
      }
    };
  }, [socket, connected, handlers]);

  return {
    socket,
    connected,
    connect,
    disconnect,
    reconnect,
  };
}

/**
 * Hook for listening to a specific chat's events
 * Automatically filters events by chatId
 *
 * @example
 * ```typescript
 * useFilteredChatSocket('chat-123', {
 *   onMessageCreated: (data) => {
 *     setMessages(prev => [...prev, data]);
 *   },
 * });
 * ```
 */
export function useFilteredChatSocket(
  chatId: string | null,
  handlers: {
    onMessageCreated?: (data: ChatMessageCreatedEvent['data']) => void;
    onMessageUpdated?: (data: ChatMessageUpdatedEvent['data']) => void;
    onMessageArchived?: (data: ChatMessageArchivedEvent['data']) => void;
    onLikeAdded?: (data: ChatLikeAddedEvent['data']) => void;
    onLikeRemoved?: (data: ChatLikeRemovedEvent['data']) => void;
    onMessageSeen?: (data: ChatMessageSeenEvent['data']) => void;
  }
) {
  // Create filtered handlers that only trigger for this chatId
  const filteredHandlers: ChatEventHandlers = {
    onMessageCreated: handlers.onMessageCreated
      ? (event) => {
          if (chatId && event.data.chat_id === chatId) {
            handlers.onMessageCreated?.(event.data);
          }
        }
      : undefined,

    onMessageUpdated: handlers.onMessageUpdated
      ? (event) => {
          if (chatId && event.data.chat_id === chatId) {
            handlers.onMessageUpdated?.(event.data);
          }
        }
      : undefined,

    onMessageArchived: handlers.onMessageArchived
      ? (event) => {
          if (chatId && event.data.chat_id === chatId) {
            handlers.onMessageArchived?.(event.data);
          }
        }
      : undefined,

    onLikeAdded: handlers.onLikeAdded
      ? (event) => {
          if (chatId && event.data.chat_id === chatId) {
            handlers.onLikeAdded?.(event.data);
          }
        }
      : undefined,

    onLikeRemoved: handlers.onLikeRemoved
      ? (event) => {
          if (chatId && event.data.chat_id === chatId) {
            handlers.onLikeRemoved?.(event.data);
          }
        }
      : undefined,

    onMessageSeen: handlers.onMessageSeen
      ? (event) => {
          if (chatId && event.data.chat_id === chatId) {
            handlers.onMessageSeen?.(event.data);
          }
        }
      : undefined,
  };

  return useChatSocket({
    enabled: !!chatId,
    handlers: filteredHandlers,
  });
}
